import { useParams, Link } from 'react-router-dom'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import { formatCents, formatDateTime } from '@webmono/storefront-ui/utils'
import { useOrder } from '@/lib/hooks'

export function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const tenant = useTenantConfig()
  const { data: order, isLoading } = useOrder(orderId!)

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-16 w-16 bg-muted rounded-full mx-auto" />
          <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
          <div className="h-4 bg-muted rounded w-2/3 mx-auto" />
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h1 className="text-xl font-heading font-semibold text-foreground mb-2">Order Not Found</h1>
        <p className="text-muted-foreground mb-6">We couldn't find this order.</p>
        <Link
          to="/menu"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium"
        >
          Return to Menu
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">Order Confirmed!</h1>
        <p className="text-muted-foreground">
          Thank you for your order, {order.customer.name}
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div>
            <p className="text-sm text-muted-foreground">Order Number</p>
            <p className="font-mono font-semibold text-foreground text-lg">{order.orderNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Status</p>
            <p className="font-medium text-primary capitalize">{order.status.replace('_', ' ')}</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {order.items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span className="text-foreground">
                {item.quantity}x {item.menuItem.name}
              </span>
              <span className="text-muted-foreground">
                {formatCents(item.lineTotalCents, tenant.operations.currency)}
              </span>
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="text-foreground">{formatCents(order.totals.subtotalCents, tenant.operations.currency)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax</span>
            <span className="text-foreground">{formatCents(order.totals.taxCents, tenant.operations.currency)}</span>
          </div>
          {order.totals.tipCents > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tip</span>
              <span className="text-foreground">{formatCents(order.totals.tipCents, tenant.operations.currency)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold pt-2 border-t border-border">
            <span className="text-foreground">Total</span>
            <span className="text-foreground">{formatCents(order.totals.totalCents, tenant.operations.currency)}</span>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <h3 className="font-medium text-foreground mb-3">
          {order.fulfillmentType === 'pickup' && 'Pickup Details'}
          {order.fulfillmentType === 'delivery' && 'Delivery Details'}
          {order.fulfillmentType === 'table_service' && 'Table Service'}
        </h3>

        {order.fulfillmentType === 'pickup' && (
          <div className="space-y-2 text-sm">
            <p className="text-foreground">{tenant.contact.address}</p>
            <p className="text-foreground">{tenant.contact.city}, {tenant.contact.state} {tenant.contact.zip}</p>
            {order.estimatedReadyTime && (
              <p className="text-primary font-medium mt-3">
                Estimated ready: {formatDateTime(order.estimatedReadyTime)}
              </p>
            )}
          </div>
        )}

        {order.fulfillmentType === 'delivery' && order.deliveryAddress && (
          <div className="space-y-2 text-sm">
            <p className="text-foreground">{order.deliveryAddress.street}</p>
            {order.deliveryAddress.unit && <p className="text-foreground">Unit {order.deliveryAddress.unit}</p>}
            <p className="text-foreground">
              {order.deliveryAddress.city}, {order.deliveryAddress.province} {order.deliveryAddress.postalCode}
            </p>
            {order.estimatedDeliveryTime && (
              <p className="text-primary font-medium mt-3">
                Estimated delivery: {formatDateTime(order.estimatedDeliveryTime)}
              </p>
            )}
          </div>
        )}

        {order.fulfillmentType === 'table_service' && order.tableNumber && (
          <p className="text-sm text-foreground">
            Your order will be brought to Table {order.tableNumber}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <Link
          to={`/order/${order.id}/track`}
          className="block w-full py-3 rounded-full bg-primary text-primary-foreground font-medium text-center hover:opacity-90 transition-opacity"
        >
          Track Order
        </Link>
        <Link
          to="/menu"
          className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-medium text-center hover:bg-secondary/80 transition-colors"
        >
          Order Again
        </Link>
      </div>
    </div>
  )
}
